import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  Input,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import {
  Attribute,
  CSSClass,
  DndEvent,
  DOMEvent,
  ViewSelector,
} from './dom.constant';
import {
  Layout,
  Widget,
  Container,
  Navigation,
  Collapse,
} from '../core/dashboard.node';
import { NavigationComponent } from './navigation.component';
import { CollapseComponent } from './collapse.component';
import { DashboardService } from '../dashboard.service';
import { LayoutComponent } from './layout.component';
import { Templates } from './templates.interface';
import { take } from 'rxjs/operators';
import { DashboardNodeComponent } from './dashboard-node.component';

@Component({
  selector: 'ak-dashboard-node',
  template: `<div
    #wrapper
    class="{{ cssClass.NODE_WRAPPER }}  {{
      service.hoveredNodeId === node.id ? cssClass.MOUSE_OVER : ''
    }} {{ service.selectedNodeId === node.id ? cssClass.SELECTED : '' }}"
    [ngClass]="node.classes"
  >
    <div *ngIf="node.config.allowDrag" class="{{ cssClass.NODE_BREADCRUMBS }}">
      <ng-container
        *ngIf="templates?.breadcrumbs; else defaultBreadcrumbs"
        [ngTemplateOutlet]="templates.breadcrumbs"
        [ngTemplateOutletContext]="{ $implicit: node }"
      >
      </ng-container>
      <ng-template #defaultBreadcrumbs>
        {{ node.name }}
      </ng-template>
    </div>
    <div
      *ngIf="node.config.allowDrag && node.toolbarVisible"
      class="{{ cssClass.NODE_TOOLBAR }}"
    >
      <ng-container
        *ngIf="templates?.toolbar"
        [ngTemplateOutlet]="templates.toolbar"
        [ngTemplateOutletContext]="{ $implicit: node }"
      >
      </ng-container>
    </div>
    <div class="{{ cssClass.NODE_CONTENT }}">
      <ng-container #componentRef></ng-container>
      <ng-container [ngSwitch]="true">
        <ng-container *ngSwitchCase="isNavigation">
          <ak-dashboard-container
            [hidden]="navigation.activeNavigation?.id !== container.id"
            *ngFor="let container of navigation.containers"
            [templates]="templates"
            [service]="service"
            [node]="container"
          ></ak-dashboard-container>
        </ng-container>
        <ng-container *ngSwitchCase="isCollapse">
          <ak-dashboard-container
            [hidden]="!collapse.isExpanded"
            [node]="collapse.container"
            [templates]="templates"
            [service]="service"
          ></ak-dashboard-container>
        </ng-container>
      </ng-container>
    </div>
  </div> `,
  encapsulation: ViewEncapsulation.None,
})
export class NodeComponent implements AfterViewInit {
  @ViewChild(ViewSelector.COMPONENT, { read: ViewContainerRef })
  private componentRef!: ViewContainerRef;
  @ViewChild(ViewSelector.WRAPPER, { read: ElementRef })
  private nodeWrapper!: ElementRef;

  @Input() node!: Layout | Collapse | Widget | Navigation;
  @Input() service!: DashboardService;
  @Input() templates!: Templates;

  private component!: ComponentRef<DashboardNodeComponent<any>>;
  public readonly cssClass: typeof CSSClass = CSSClass;
  public isNavigation: boolean;
  public isCollapse: boolean;

  get navigation(): Navigation {
    return this.node as Navigation;
  }

  get collapse(): Collapse {
    return this.node as Collapse;
  }

  constructor(
    private factory: ComponentFactoryResolver,
    public changeRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private el: ElementRef
  ) {
    this.isNavigation = false;
    this.isCollapse = false;
  }

  ngAfterViewInit(): void {
    this.createComponent();
    this.renderer.addClass(
      this.nodeWrapper.nativeElement,
      this.node.component.cssClassName
    );
    if (this.node.config.allowDrag) {
      this.listenToMouseEvents();
      if (this.node.isDraggable) {
        this.listenToDragEvents();
      }
    }
  }

  private createComponent(): void {
    this.componentRef.clear();
    this.component = this.componentRef.createComponent(
      this.factory.resolveComponentFactory(this.node.component.class)
    );

    this.isNavigation = this.component.instance instanceof NavigationComponent;
    this.isCollapse = this.component.instance instanceof CollapseComponent;
    this.component.instance.editMode = this.node.config.allowDrag
      ? true
      : false;
    this.component.instance.dashboardWidth = this.service.dashboardWidth;
    this.component.instance.dragFocusTimeout =
      this.node.config.dragFocusTimeout || 500;
    this.component.instance.node = this.node;
    if (this.component.instance instanceof LayoutComponent) {
      this.initLayout(this.component.instance, this.node as Layout);
    }

    this.changeRef.detectChanges();
  }

  private initLayout(component: LayoutComponent, node: Layout): void {
    component.onViewInit.pipe(take(1)).subscribe(() => {
      component.containers?.forEach((vcr: ViewContainerRef, index: number) => {
        vcr.clear();

        if (node.containers[index]) {
          const container = vcr.createComponent(
            this.factory.resolveComponentFactory(ContainerComponent)
          ).instance;
          container.node = node.containers[index];
          container.templates = this.templates;
          container.service = this.service;
          this.changeRef.detectChanges();
        }
      });
    });
  }

  private listenToMouseEvents(): void {
    this.nodeWrapper.nativeElement.addEventListener(
      DOMEvent.CLICK,
      ($event: Event) => {
        $event.stopPropagation();
        if (this.service.setSelectedNode(this.node.id)) {
          this.node.config.selected?.call(null, this.node);
        }
      }
    );

    this.nodeWrapper.nativeElement.addEventListener(
      DOMEvent.MOUSE_OVER,
      this.mouseoverHandler.bind(this)
    );

    this.nodeWrapper.nativeElement.addEventListener(
      DOMEvent.MOUSE_LEAVE,
      this.mouseLeaveHandler.bind(this)
    );
  }

  private listenToDragEvents(): void {
    this.renderer.setAttribute(
      this.nodeWrapper.nativeElement,
      Attribute.DRAGGABLE,
      'true'
    );

    this.renderer.addClass(this.nodeWrapper.nativeElement, Attribute.DRAGGABLE);

    this.nodeWrapper.nativeElement.addEventListener(
      DOMEvent.DRAG_START,
      this.dragStartHandler.bind(this)
    );
    this.nodeWrapper.nativeElement.addEventListener(
      DOMEvent.DRAG_END,
      this.dragEndHandler.bind(this)
    );
  }

  private mouseoverHandler($event: Event): void {
    $event.stopPropagation();
    this.service.setHoveredNode(this.node.id);
  }

  private mouseLeaveHandler($event: Event): void {
    $event.stopPropagation();
    this.service.unsetHoveredNode();
  }

  private dragStartHandler($event: DndEvent): void {
    $event.stopPropagation();
    this.component.changeDetectorRef.detach();
    this.renderer.addClass(this.nodeWrapper.nativeElement, CSSClass.DRAGGING);
    $event.dataTransfer.setDragImage(this.getDragPreview(), 0, 0);
    this.node.config.dragStart?.call(null, this.node);
    this.service.setDraggingNode(this.node);
  }

  private dragEndHandler($event: Event): void {
    $event.stopPropagation();
    this.component.changeDetectorRef.reattach();
    this.renderer.removeClass(
      this.nodeWrapper.nativeElement,
      CSSClass.DRAGGING
    );
    this.node.config.dragEnd?.call(null, this.node);
    this.service.unsetDraggingNode();
  }

  private getDragPreview(): HTMLElement {
    return this.el.nativeElement.querySelector(`.${CSSClass.NODE_BREADCRUMBS}`);
  }
}

// ===================== CONTAINER ======================== //
//    To avoid cyclic dependency injection on compilation   //
// ======================================================== //

@Component({
  selector: 'ak-dashboard-container',
  template: `<div
    #container
    class="{{ cssClass.CONTAINER }}"
    [ngClass]="{ empty: node.items.length === 0 }"
  >
    <div *ngIf="node.config.allowDrag" class="{{ cssClass.CONTAINER_TOOLBAR }}">
      {{ node.name }}
    </div>
    <ng-container *ngFor="let item of node?.items; let i = index">
      <div
        *ngIf="node.isDroppable && this.node.config.allowDrag"
        akDroppable
        [animated]="node.config.dropslotAnimation"
        (onDrop)="onDrop($event, i)"
        (dragEnter)="onDragEnter()"
        (dragLeave)="onDragLeave()"
      ></div>
      <ak-dashboard-node
        [id]="node.id"
        [node]="item"
        [service]="service"
        [templates]="templates"
      ></ak-dashboard-node>
    </ng-container>
    <div
      *ngIf="node.isDroppable && this.node.config.allowDrag"
      akDroppable
      [animated]="node.config.dropslotAnimation"
      (onDrop)="onDrop($event, node.items.length)"
      (dragEnter)="onDragEnter()"
      (dragLeave)="onDragLeave()"
    ></div>
  </div> `,
  encapsulation: ViewEncapsulation.None,
})
export class ContainerComponent implements AfterViewInit {
  @ViewChild(ViewSelector.CONTAINER, { read: ElementRef })
  private containerWrapper!: ElementRef;
  @Input() service!: DashboardService;
  @Input() templates!: Templates;
  @Input() node!: Container;

  public cssClass: typeof CSSClass = CSSClass;

  constructor(
    public changeRef: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    const containerClassName = (<Layout | Collapse | Widget>this.node.parent)
      ?.component?.cssClassName;
    if (containerClassName) {
      this.renderer.addClass(
        this.containerWrapper.nativeElement,
        `${containerClassName}-container`
      );
    }
    this.changeRef.detectChanges();
  }

  public onDragEnter(): void {
    this.renderer.addClass(
      this.containerWrapper.nativeElement,
      CSSClass.DRAG_ENTER
    );
  }

  public onDragLeave(): void {
    this.renderer.removeClass(
      this.containerWrapper.nativeElement,
      CSSClass.DRAG_ENTER
    );
  }

  public onDrop($event: any, index: number): void {
    if (this.service.draggingNode) {
      this.service.draggingNode.moveTo(this.node, index);
      this.service.unsetDraggingNode();
    } else if ($event) {
      this.node.addNode($event, index);
    }
  }
}
